import React, { Component } from "react";
import { LocaleValue } from "@eres/core/dist/LocaleValue";
import BaseDropDown, { Props as BProps } from "./BaseDropDown";
import { LocaleUtil } from "@eres/core";
import { LVStrings } from "@my-dld/assets/strings";

interface Props<T> {
	items: T[];
	title: string | LocaleValue;
	id: string;
	formatter: ( item: T ) => string;
	toId: ( item: T ) => string | number;
	values?: T[];
	onChange?: ( values: T[] ) => void;
	showAllOption?: boolean;
	allLabel?: string | LocaleValue;
	allDeselectLabel?: string | LocaleValue;
	baseProps?: Partial<BProps>;
	showChips?: boolean;
	maxChips?: number;
}

interface State<T> {
}

class MultiChoiceDropDown<T> extends Component<Props<T>, State<T>> {
	static defaultProps: Partial<Props<any>> = {
		allLabel: LVStrings.select_all,
		allDeselectLabel: LVStrings.un_select_all,
		maxChips: 3,
	};

	constructor( props: Props<T> ) {
		super( props );
		this.state = {
			selectedItems: props.values ? props.values : [],
		};
	}

	handleItemChange = ( item: T, checked: boolean ) => {
		const { toId, values } = this.props;
		let selectedItems: T[] = [];
		if ( checked ) {
			selectedItems = values ? [ ...values, item ] : [ item ];
		} else {
			selectedItems = values?.filter( x => toId( x ) !== toId( item ) ) || [];
		}
		this.props.onChange?.( selectedItems );
	};
	onReset = () => {
		this.props.onChange?.( [] );
	};
	handleAllChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
		const { items } = this.props;
		const checked = e.currentTarget.checked;
		let selectedItems: T[] = [];
		if ( checked ) {
			selectedItems = [ ...items ];
		} else {
			selectedItems = [];
		}
		this.props.onChange?.( selectedItems );
	};

	render() {
		const {
			title,
			items,
			formatter,
			toId,
			id,
			values,
			showAllOption,
			allLabel,
			allDeselectLabel,
			baseProps, showChips, maxChips,
		} = this.props;
		const isAll = items?.length === values?.length;
		const Info = showChips ? <Chips
			toId={ toId }
			items={ values ?? [] } formatter={ formatter } maxCount={ maxChips! }/> : <></>;
		return (
			<BaseDropDown
				info={ Info }
				{ ...baseProps } title={ title } id={ id } onReset={ this.onReset }>
				<ul className="dropdownList" aria-labelledby={ id }>
					{ showAllOption &&
						<li key={ "all" } className="checkbox">
							<label>
								<input
									checked={ isAll } type="checkbox"
									onChange={ this.handleAllChange }/>
								{ LocaleUtil.getLocalValue( isAll ? allDeselectLabel : allLabel ) }
							</label>
						</li>
					}
					{ items.map( ( item, index ) => {
						const handler = ( e: React.ChangeEvent<HTMLInputElement> ) => {
							this.handleItemChange( item, e.currentTarget.checked );
						};
						const isChecked = values?.indexOf( item ) !== -1;
						return <li key={ toId( item ) } className="checkbox">
							<label><input
								checked={ isChecked } type="checkbox"
								onChange={ handler }/>{ formatter( item ) }</label>
						</li>;
					} ) }
				</ul>
			</BaseDropDown>
		);
	}
}

function Chips<T>( props: {
	items: T[],
	formatter: ( item: T ) => string,
	maxCount: number,
	toId: ( item: T ) => string | number;
} ) {
	const { items, formatter, maxCount, toId } = props;
	const displayItems = items.slice( 0, maxCount );
	const isMore = items.length - displayItems.length;
	return <div className={ "chip-wrapper" }>
		{ displayItems.map( value =>
			<span key={ toId( value ) } className="badge">{ formatter( value ) }</span> )
		}
		{ isMore > 0 && <span key={ "more" } className="badge badge-more">{ " + " + isMore }</span> }
	</div>;
}

export default MultiChoiceDropDown;
